import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../components/CourierEntry.css';
import { ToastContainer, toast } from 'react-toastify';
import { hasPermission } from '../utils/permissions';

// Use the same API base you use in Postman
const API_BASE = 'https://newerp.awasthivitality.com/api';

// External sources for dropdowns
const COURIER_API = 'https://newerp.awasthivitality.com/api/couriers';
const PATIENTS_API = 'https://newerp.awasthivitality.com/api/patients';

export default function CourierForm() {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [couriers, setCouriers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [patientSearch, setPatientSearch] = useState('');
  const [isPatientOpen, setIsPatientOpen] = useState(false);
  const { patient_id: routePatientId } = useParams?.() || {};
  const [form, setForm] = useState({
    patient_id: '',
    courier_id: '',
    delivery_date: '',
    contact_name: '',
    contact_mobile: '',
    tracking_no: '',
    tracking_id: '',
    mop: 'ONLINE',
    course: '',
    delivery_courceid: '',
  });
  const [file, setFile] = useState(null);
  const [hasAccess, setHasAccess] = useState(true);

  useEffect(() => {
    const checkPerm = async () => {
      const canAdd = await hasPermission('delivery', 'adddelivery');
      if (!canAdd) {
        toast.error('You do not have permission to add courier entries.');
        setTimeout(() => navigate('/CourierEntryList'), 2000);
        setHasAccess(false);
      } else {
        setHasAccess(true);
      }
    };
    checkPerm();
    
    // Listen for permission updates
    const handlePermissionUpdate = () => {
      checkPerm();
    };
    window.addEventListener('permissionsUpdated', handlePermissionUpdate);
    return () => window.removeEventListener('permissionsUpdated', handlePermissionUpdate);
  }, [navigate]);

  useEffect(() => {
    (async () => {
      try {
        const pUrl = new URL(PATIENTS_API);
        pUrl.searchParams.set('page', '1');
        pUrl.searchParams.set('per_page', '100');

        const cUrl = COURIER_API;

    const token = (localStorage.getItem('session_token') || '').trim();

const [pRes, cRes] = await Promise.all([
  fetch(pUrl.toString(), {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(token ? { 'X-Session-Token': token } : {}),
    },
  }),
  fetch(cUrl, {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(token ? { 'X-Session-Token': token } : {}),
    },
  }),
]);


        const pJson = await pRes.json().catch(() => ({}));
        const cJson = await cRes.json().catch(() => ({}));
let plist = [];

if (Array.isArray(pJson?.data?.patients?.data)) {
  plist = pJson.data.patients.data;
} else if (Array.isArray(pJson?.data?.patients)) {
  plist = pJson.data.patients;
} else if (Array.isArray(pJson?.data)) {
  plist = pJson.data;
} else if (Array.isArray(pJson)) {
  plist = pJson;
}

setPatients(plist);

        const cdata = cJson?.data || cJson;
        const clist = Array.isArray(cdata) ? cdata : [];

        setPatients(plist);
        setCouriers(clist);
        
        // Auto-select the last added courier (first in the list since backend orders by courier_id DESC)
        if (clist.length > 0) {
          setForm((prev) => {
            // Only auto-select if no courier is currently selected
            if (!prev.courier_id) {
              return { ...prev, courier_id: String(clist[0].courier_id) };
            }
            return prev;
          });
        }
      } catch {
        // ignore errors on preload
      }
    })();
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Keep both 'course' (diagnosis_id) and 'delivery_courceid' (patient_course_id) in sync
  const handleCourseSelect = (e) => {
    const selectedId = e.target.value;
    const selected = courses.find((c) => String(c.patient_course_id) === String(selectedId));
    setForm({
      ...form,
      course: selected?.diagnosis_id ? String(selected.diagnosis_id) : '',
      delivery_courceid: selected ? String(selected.patient_course_id) : '',
    });
  };

  const handleFile = (e) => {
    setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null);
  };

  // If navigated from /patient/details/:patient_id, pre-fill patient
  useEffect(() => {
    if (routePatientId && !form.patient_id) {
      setForm((prev) => ({ ...prev, patient_id: String(routePatientId) }));
    }
  }, [routePatientId]);
useEffect(() => {
  if (!isPatientOpen) {
    setPatientSearch('');
  }
}, [isPatientOpen]);

  // Load patient's courses whenever patient changes
  useEffect(() => {
    const loadCourses = async () => {
      if (!form.patient_id) {
        setCourses([]);
        // also clear selection to avoid sending 0
        setForm((prev) => ({ ...prev, course: '', delivery_courceid: '' }));
        return;
      }
      try {
        const token = (localStorage.getItem('session_token') || '').trim();
        // Try /patient/view first (same as PatientDetails), with auth headers
        const viewUrl = `https://newerp.awasthivitality.com/api/patient/view/${form.patient_id}${token ? `?token=${encodeURIComponent(token)}` : ''}`;
        let resp = await fetch(viewUrl, {
          headers: {
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
            ...(token ? { 'X-Session-Token': token } : {}),
          }
        });
        // Fallback to /patient/details if needed
        if (!resp.ok) {
          resp = await fetch(`https://newerp.awasthivitality.com/api/patient/details/${form.patient_id}`);
        }
        const json = await resp.json().catch(() => ({}));
        const d = json?.data || {};
        let list = [];
        if (Array.isArray(d.patient_courses)) list = d.patient_courses;
        else if (Array.isArray(d.courses)) list = d.courses;
        else if (Array.isArray(d.course_data)) list = d.course_data;

        // Normalize to match PatientDetails: include primary and secondary diagnosis rows
        const normalized = [];
        (Array.isArray(list) ? list : []).forEach((c) => {
          const primaryId = c.patient_course_id ?? c.id ?? c.course_id;
          const primaryName = c.diagnosis || c.diagnosis_name || c.name || c.diagnosis?.name;
          const primaryDiagId = c.diagnosis_id ?? c.diagnosis?.diagnosis_id ?? c.diagnosisId ?? c.diagnosis_code_id;
          if (primaryId && (primaryName || primaryDiagId)) {
            normalized.push({ patient_course_id: String(primaryId), diagnosis_id: primaryDiagId ? String(primaryDiagId) : '', diagnosis_label: primaryName || '' });
          }
          // Some payloads contain second diagnosis with *_1 fields
          const secondaryId = c.patient_course_id1;
          const secondaryName = c.diagnosis1;
          const secondaryDiagId = c.diagnosis_id1;
          if (secondaryId && (secondaryName || secondaryDiagId)) {
            normalized.push({ patient_course_id: String(secondaryId), diagnosis_id: secondaryDiagId ? String(secondaryDiagId) : '', diagnosis_label: secondaryName || '' });
          }
        });
        setCourses(normalized);

        // Auto-select the last added course (first in the list since backend orders by patient_course_id DESC)
        if (normalized.length > 0) {
          const lastCourse = normalized[0]; // First one is the latest
          setForm((prev) => {
            // Only auto-select if no course is currently selected or current selection is invalid
            if (!prev.delivery_courceid || !normalized.some((c) => String(c.patient_course_id) === String(prev.delivery_courceid))) {
              return {
                ...prev,
                course: lastCourse.diagnosis_id || '',
                delivery_courceid: String(lastCourse.patient_course_id)
              };
            }
            return prev;
          });
        } else {
          // If no courses, clear selection
          setForm((prev) => ({ ...prev, course: '', delivery_courceid: '' }));
        }
      } catch (err) {
        setCourses([]);
        setForm((prev) => ({ ...prev, course: '', delivery_courceid: '' }));
      }
    };
    loadCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.patient_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { patient_id, courier_id, delivery_date, contact_name, contact_mobile, tracking_no, tracking_id, course, delivery_courceid } = form;
    if (!patient_id || !courier_id || !delivery_date || !contact_name || !contact_mobile || !tracking_no || !delivery_courceid) {
      toast.warn('Please fill all required fields.');
      return;
    }

    const fd = new FormData();

    // Helpers to avoid DB collation issues (latin1) by keeping ASCII-only names
    const toAscii = (str) => {
      try {
        return (str || '')
          .normalize('NFKD')
          .replace(/[\u0300-\u036f]/g, '') // remove diacritics
          .replace(/[\u00A0\u2000-\u200B\u202F\u205F\u3000]/g, ' ') // unicode spaces -> space
          .replace(/[^\x00-\x7F]/g, ''); // drop non-ASCII
      } catch {
        return (str || '').replace(/[^\x00-\x7F]/g, '');
      }
    };

    const sanitizeFileName = (name) => {
      const ascii = toAscii(name);
      const lastDot = ascii.lastIndexOf('.');
      const base = (lastDot > 0 ? ascii.slice(0, lastDot) : ascii) || 'file';
      const ext = lastDot > 0 ? ascii.slice(lastDot + 1) : '';
      const cleanBase = base
        .replace(/[^A-Za-z0-9._ -]+/g, '_')
        .replace(/[\s]+/g, ' ')
        .trim()
        .replace(/[\s]+/g, '_')
        .replace(/_+/g, '_');
      const cleanExt = ext.replace(/[^A-Za-z0-9]+/g, '');
      return cleanExt ? `${cleanBase}.${cleanExt}` : cleanBase;
    };
    // API expects delivery table field names
    fd.append('patient_id', patient_id);
    fd.append('courier_id', courier_id);
    fd.append('delivery_date', delivery_date);
    fd.append('contact_name', contact_name);
    fd.append('contact_mobile', contact_mobile);
    fd.append('tracking_no', tracking_no);
    // optional/legacy fields (backend provides safe defaults when empty)
    fd.append('mop', form.mop || 'ONLINE');
    // Align with backend columns
    const derivedPayment = (form.mop || 'ONLINE') === 'COD' ? 'NOT PAID' : 'PAID';
    fd.append('payment_status', derivedPayment);
    fd.append('status', 'UNDELIVERED');
    fd.append('delivery_status', 'Enabled');
    // Make sure backend receives selected course values
    fd.append('course', course || '');
    fd.append('delivery_courceid', delivery_courceid || '');
    fd.append('location', '');
    fd.append('code', '');
    fd.append('remark', ''); // optional, leave empty by default
    if (tracking_id) fd.append('tracking_id', tracking_id); // included only if user entered it
    if (file) {
      // Rename file to ASCII-safe name so backend saves a compatible path
      const safeName = sanitizeFileName(file.name || 'upload');
      const safeFile = new File([file], safeName, { type: file.type || 'application/octet-stream' });
      // Send exactly as backend expects
      fd.append('invoice', safeFile);
      // Provide metadata so backend can persist name/mime consistently
      fd.append('name', safeName);
      if (file.type) fd.append('mime', file.type);
    }

    try {
      const token = (localStorage.getItem('session_token') || '').trim();
      const url = `${API_BASE}/create-courier-entry${token ? `?token=${encodeURIComponent(token)}` : ''}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          ...(token ? { 'X-Session-Token': token } : {}),
        },
        body: fd
      });
      const res = await response.json().catch(() => ({ success: false }));
     if (response.ok && res && res.success !== false) {
  toast.success(res.msg || res.message || 'Courier entry saved');

  // Optional: short delay so toast is visible before redirect
  setTimeout(() => {
    navigate('/CourierEntryList');
  }, 1500);

  // Reset form (optional, since we redirect anyway)
  setForm({
    patient_id: '',
    courier_id: '',
    delivery_date: '',
    contact_name: '',
    contact_mobile: '',
    tracking_no: '',
    tracking_id: '',
    mop: 'ONLINE'
  });
  setFile(null);
}
else {
        const firstError = res?.errors && typeof res.errors === 'object'
          ? Object.values(res.errors).flat()[0]
          : null;
        toast.error(firstError || res?.message || res?.msg || `Create failed (HTTP ${response.status})`);
      }
    } catch (err) {
      console.error('Create courier network error:', err);
      toast.error('❌ Network Error. Please check your connection and try again.');
    }
  };

  // const filteredPatients = patients.filter((p) => {
  //   if (!patientSearch.trim()) return true;
  //   const q = patientSearch.trim().toLowerCase();
  //   return (
  //     String(p.pr_no || p.patient_id || '').toLowerCase().includes(q) ||
  //     String(p.name || '').toLowerCase().includes(q) ||
  //     String(p.mobile || '').toLowerCase().includes(q)
  //   );
  // });

  const selectedPatientLabel = () => {
    const p = patients.find((x) => String(x.patient_id) === String(form.patient_id));
    if (!p) return 'Select Patient';
    return `${p.pr_no || p.patient_id} / ${p.name} / ${p.mobile}`;
  };

  if (!hasAccess) {
    return (
      <div className="form-wrapper">
        <div className="form-card">
          <div style={{ padding: 20, textAlign: 'center' }}>
            <h3>Access Denied</h3>
            <p>You do not have permission to access this page.</p>
          </div>
        </div>
      </div>
    );
  }

  // no-op placeholder removed: patient selection uses select + filter
const searchPatients = async (query) => {
  try {
    const token = (localStorage.getItem('session_token') || '').trim();
    const q = query.trim();

    if (!q) {
      setPatients([]);
      return;
    }

    // Detect search type
    let searchParam = q;

    // If digits only
    if (/^\d+$/.test(q)) {
      if (q.length <= 5) {
        // patient_id / PR No search
        searchParam = q;
      } else {
        // mobile number
        searchParam = q;
      }
    }

    const res = await fetch(
      `${PATIENTS_API}?search=${encodeURIComponent(searchParam)}&per_page=20`,
      {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(token ? { 'X-Session-Token': token } : {}),
        },
      }
    );

    const json = await res.json();

    let list = [];

    if (Array.isArray(json?.data?.patients?.data)) {
      list = json.data.patients.data;
    } else if (Array.isArray(json?.data?.patients)) {
      list = json.data.patients;
    } else if (Array.isArray(json?.data)) {
      list = json.data;
    }

const qLower = q.toLowerCase();

const sorted = [...list].sort((a, b) => {
  const aPr = String(a.pr_no || '').toLowerCase();
  const bPr = String(b.pr_no || '').toLowerCase();

  // Exact match first
  if (aPr === qLower && bPr !== qLower) return -1;
  if (bPr === qLower && aPr !== qLower) return 1;

  // Starts with next
  if (aPr.startsWith(qLower) && !bPr.startsWith(qLower)) return -1;
  if (bPr.startsWith(qLower) && !aPr.startsWith(qLower)) return 1;

  return 0;
});

setPatients(sorted);
  } catch (err) {
    console.error('Patient search error', err);
  }
};


  return (
    <div className="form-wrapper">
      <div className="form-card">
        <div className="form-header">Courier Entry</div>
        <form className="form-grid" onSubmit={handleSubmit}>
          <div className="form-item">
            <label>Courier Date<span className="req">*</span></label>
            <input type="date" name="delivery_date" value={form.delivery_date} onChange={handleChange} required />
          </div>

          <div className="form-item">
            <label>Contact Name<span className="req">*</span></label>
            <input type="text" name="contact_name" placeholder="Enter Contact Name" value={form.contact_name} onChange={handleChange} required />
          </div>

          <div className="form-item">
            <label>Courier<span className="req">*</span></label>
            <select name="courier_id" value={form.courier_id} onChange={handleChange} required>
              <option value="">Select</option>
              {couriers.map(c => (
                <option key={c.courier_id} value={c.courier_id}>{c.courier_name}</option>
              ))}
            </select>
          </div>

          <div className="form-item">
            <label>Contact Mobile<span className="req">*</span></label>
            <input type="tel" name="contact_mobile" placeholder="Contact Mobile" value={form.contact_mobile} onChange={handleChange} required />
          </div>

          <div className="form-item">
            <label>Mode of Payment<span className="req">*</span></label>
            <select name="mop" value={form.mop} onChange={handleChange} required>
              <option value="ONLINE">ONLINE</option>
              <option value="COD">COD</option>
            </select>
          </div>
        

          <div className="form-item" style={{ position: 'relative' }}>
            <label>Patient<span className="req">*</span></label>
            {/* Hidden select to satisfy form required/validation */}
            <select name="patient_id" value={form.patient_id} onChange={handleChange} required style={{ display: 'none' }}>
              <option value="">Select</option>
              {patients.map(p => (
                <option key={p.patient_id} value={p.patient_id}>
                  {p.pr_no || p.patient_id} / {p.name} / {p.mobile}
                </option>
              ))}
            </select>
            {/* Custom dropdown */}
            <button
              type="button"
              onClick={() => setIsPatientOpen((v) => !v)}
              style={{ width: '100%', textAlign: 'left', padding: '8px', border: '1px solid #ccc', borderRadius: 4, background: '#fff' }}
            >
              {selectedPatientLabel()}
              <span style={{ float: 'right' }}>{isPatientOpen ? '▲' : '▼'}</span>
            </button>
            {isPatientOpen && (
              <div
                style={{
                  position: 'absolute',
                  zIndex: 10,
                  top: '100%',
                  left: 0,
                  right: 0,
                  background: '#fff',
                  border: '1px solid #ccc',
                  borderRadius: 4,
                  marginTop: 4,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                }}
              >
                <div style={{ padding: 8 }}>
                <input
  type="text"
  placeholder="Search Patient ID / Name / Mobile"
  value={patientSearch}
  onChange={(e) => {
    const value = e.target.value;
    setPatientSearch(value);

    if (value.length >= 1) {
      searchPatients(value); // 🔥 works from 1 digit
    }
  }}
  autoFocus
/>


                </div>
                <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                  {/* {filteredPatients.length === 0 && (
                    <div style={{ padding: '8px 12px', color: '#888' }}>No results</div>
                  )} */}
              {patients.length === 0 && patientSearch && (
  <div style={{ padding: '8px 12px', color: '#888' }}>
    No matching patient found
  </div>
)}


{patients.map((p) => (
                    <div
                      key={p.patient_id}
                      onClick={() => {
                        setForm((prev) => ({ ...prev, patient_id: String(p.patient_id) }));
                        setIsPatientOpen(false);
                      }}
                      style={{ padding: '8px 12px', cursor: 'pointer' }}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      {p.pr_no || p.patient_id} / {p.name} / {p.mobile}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
  <div className="form-item">
            <label>Course<span className="req">*</span></label>
            <select name="delivery_courceid" value={form.delivery_courceid} onChange={handleCourseSelect} required>
              <option value="">Select</option>
              {courses.map((c) => (
                <option key={c.patient_course_id} value={c.patient_course_id}>
                  {c.diagnosis_label || `Course ${c.patient_course_id}`}
                </option>
              ))}
            </select>
          </div>
          <div className="form-item">
            <label>Tracking ID<span className="req">*</span></label>
            <input type="text" name="tracking_no" placeholder="Tracking ID" value={form.tracking_no} onChange={handleChange} required />
          </div>

          {/* <div className="form-item">
            <label>Alt Tracking (optional)</label>
            <input type="text" name="tracking_id" placeholder="Tracking Alt ID" value={form.tracking_id} onChange={handleChange} />
          </div> */}

          <div className="form-item full-width">
            <label>Upload PDF</label>
            <input type="file" name="invoice" accept="application/pdf,image/*" onChange={handleFile} />
          </div>

          <div className="form-actions full-width">
            <button type="submit" className="btn-save">Save</button>
          </div>
        </form>
      </div>
        <ToastContainer 
      position="top-right"
      autoClose={3000}
      hideProgressBar={false}
      newestOnTop={false}
      closeOnClick
      pauseOnHover
      draggable
      theme="colored"
    />
    </div>
  );
}